export const OAUTH_POPUP_NAME = "replyn-oauth";
const OAUTH_MESSAGE_SOURCE = "replyn-oauth-result";

export interface OAuthPopupResult {
  code?: string;
  error?: string;
  errorReason?: string;
  errorDescription?: string;
}

interface OAuthPopupMessage extends OAuthPopupResult {
  source: typeof OAUTH_MESSAGE_SOURCE;
}

/**
 * Runs before React mounts. A provider redirect lands the popup back on our
 * own origin, where booting the whole SPA just to read one query param would
 * be a waste - and the opener is the window that actually needs the code.
 */
export const relayOAuthResultToOpener = (): boolean => {
  if (!window.opener) return false;

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");

  // Keyed on the credential rather than on `window.name`, which some browsers
  // drop across a cross-origin redirect. A tab opened from our own app never
  // carries an OAuth code, so this cannot fire on a real app window.
  if (!code && !error) return false;

  const message: OAuthPopupMessage = {
    source: OAUTH_MESSAGE_SOURCE,
    code: code ?? undefined,
    error: error ?? undefined,
    errorReason: params.get("error_reason") ?? undefined,
    errorDescription: params.get("error_description") ?? undefined,
  };

  window.opener.postMessage(message, window.location.origin);
  window.close();

  return true;
};

export class OAuthPopupBlockedError extends Error {
  constructor() {
    super(
      "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.",
    );
    this.name = "OAuthPopupBlockedError";
  }
}

export class OAuthPopupClosedError extends Error {
  constructor() {
    super("Sign-in was cancelled.");
    this.name = "OAuthPopupClosedError";
  }
}

const POPUP_FEATURES = "popup=yes,width=600,height=780";
const CLOSE_POLL_MS = 500;

/**
 * Opens the provider's consent screen in a popup and resolves with whatever it
 * redirects back with. The main window keeps its state, so there is no SPA
 * reboot and no re-authentication on the way back.
 */
export const openOAuthPopup = (url: string): Promise<OAuthPopupResult> =>
  new Promise((resolve, reject) => {
    const popup = window.open(url, OAUTH_POPUP_NAME, POPUP_FEATURES);

    if (!popup) {
      reject(new OAuthPopupBlockedError());

      return;
    }

    let settled = false;

    const cleanup = () => {
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearInterval(closeTimer);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data as OAuthPopupMessage | undefined;

      if (data?.source !== OAUTH_MESSAGE_SOURCE) return;

      cleanup();
      resolve(data);
    };

    // The popup closing without a message is a cancel. Polling is the only way
    // to see it - a cross-origin popup fires no event we can subscribe to.
    const closeTimer = window.setInterval(() => {
      if (settled || !popup.closed) return;

      cleanup();
      reject(new OAuthPopupClosedError());
    }, CLOSE_POLL_MS);

    window.addEventListener("message", onMessage);
  });
